<template>
  <div class="max-container space-y-12" v-if="!isLoggedIn">
    <GuestBanner
      title="Order quickly and save time with Lightning Order"
      description="Everything from our store available one page."
      button="Join Now"
    />

    <div class="save-time">
      <div>
        <img src="@/assets/images/save-time.png" alt="Save Time" />
      </div>
      <div class="info">
        <h3>Saves time, so you can get back to running your business</h3>
        <p>
          We know you love hanging out with us, but probably have better things
          to do.
        </p>
        <router-link class="link" to="/join-us">
          Join Now
        </router-link>
      </div>
    </div>
    <TestimonialSlider />
  </div>
  <div class="brand w-full pb-10" v-else>
    <div class="collection-banner-container" style="background-color: #342456">
      <Banner
        title="Lightning Order"
        logo="/images/lightning.svg"
        items="The fastest way to add items to your order."
        class="lightning-banner"
      >
        <template #header>
          <ol role="list" class="breadcrumb">
            <li>
              <div class="breadcrumb-item">
                <router-link to="/">Home</router-link>
              </div>
            </li>
            <li>
              <div class="breadcrumb-item">
                <span>/&nbsp;</span>
                <a href="#">Lightning Order</a>
              </div>
            </li>
          </ol>
        </template>
      </Banner>
    </div>
    <!-- Listing -->

    <div class="lightning-listing">
      <div class="flex-grow min-w-0">
        <div class="listing-header">
          <h3>Add products to your order</h3>
          <div class="listing-header-actions">
            <button ref="importFileTrigger" type="button" class="listing-import-action" :disabled="pageBusy" @click="$refs.orderImport.chooseFile()"><Icon name="document-file" aria-hidden="true" />Import file</button>
            <button type="button" class="listing-import-action" :disabled="pageBusy" @click="$refs.orderImport.openPaste()">Paste SKUs</button>
            <Menu as="div" class="relative">
              <MenuButton class="listing-import-action is-icon" aria-label="Order list options"><DotsHorizontalIcon aria-hidden="true" /></MenuButton>
              <MenuItems class="listing-options-menu">
                <MenuItem v-slot="{ active }"><button type="button" :class="{ 'is-active': active }" @click="downloadCsvTemplate()"><Icon name="download" aria-hidden="true" />Download template</button></MenuItem>
                <MenuItem v-if="selectedItems.length" v-slot="{ active }"><button type="button" :class="{ 'is-active': active }" @click="exportOrder"><Icon name="document-file" aria-hidden="true" />Export this list</button></MenuItem>
              </MenuItems>
            </Menu>
          </div>
        </div>
        <div class="flex flex-col min-w-0">
          <CsvImportResults v-if="csvImportResult && !csvResultsDismissed" ref="importResults" :result="csvImportResult" :can-undo="canUndoImport" :busy="pageBusy" :find-matches="findCsvMatches" :get-match-state="getCsvMatchState" :notify-ids="notifyProduct" :notifying-ids="notifyingIds"
            @undo="undoImport" @dismiss="csvResultsDismissed = true" @use-match="useCsvMatch" @search="$refs.productSearch.openSearch($event)" @notify="Notify" @notify-all="notifyAll" />
          <LightningOrderImport ref="orderImport" :expanded="!selectedItems.length || showImportPanel" :busy="pageBusy" :import-file="importCsvFile" :import-text="importPastedOrder" @expand="showImportPanel = true" @collapse="showImportPanel = false" @import-start="clearImportResult" />
          <div class="listing-contents" :inert="pageBusy || undefined" @dragover.prevent @drop.prevent="$refs.orderImport.dropFile($event)">
            <table v-if="selectedItems.length">
              <thead>
                <tr>
                  <th scope="col">PRODUCT</th>
                  <th scope="col" width="40" class="hidden xl:table-cell"></th>
                  <th scope="col" width="150" class="hidden xl:table-cell">
                    QUANTITY
                  </th>
                  <th scope="col" width="100" class="hidden xl:table-cell">
                    PRICE
                  </th>
                  <th scope="col" width="180" class="hidden xl:table-cell">
                    TOTAL
                  </th>
                </tr>
              </thead>
              <tbody v-for="row in selectedItems" :key="row.id">
                <Disclosure as="template">
                  <tr :id="'item_id_' + row.id">
                    <td class="name">
                      <div class="flex items-center">
                        <div class="item-info">
                          <div class="inner">
                            <img :src="row.imageThumbnailUrl" alt="" />
                            <router-link :to="'/products/' + row.slug">
                              <div class="flex-grow">
                                <p>{{ row.name }}</p>

                                <div
                                  class="flex flex-col md:flex-row md:items-center gap-1.5 pt-1"
                                >
                                  <span v-if="row.importSource" class="import-source-badge">{{ row.importSource === "Pasted SKUs" ? "From paste" : "From file" }}</span>
                                  <span class="flex-wrap">
                                    {{ row.brand }} -
                                    {{ row.product_type }}
                                  </span>
                                  <!-- <div
                                    v-if="row.outofStock"
                                    :id="row.id"
                                    class="text-gray-300 2xl:hidden"
                                  >
                                    <span class="hidden md:inline-block pr-0.5">&#x2022;</span>
                                    <span class="error">
                                      {{
                                      row.outofStock ?? ""
                                      }}
                                    </span>
                                  </div> -->
                                </div>
                              </div>
                            </router-link>
                          </div>
                          <ul class="on-mobile">
                            <li
                              class="flex items-center justify-between bg-gray-50 rounded px-4 py-2"
                            >
                              <p class="font-medium text-theme-secondary">
                                Price: ${{ commaFormat(getBulkPrice(row)) }}
                              </p>
                              <PlusMinusInput
                                :label="itemLabel(row)"
                                v-model:modelValue="row.qty"
                                :max="quantityMax(row)"
                                :min="1"
                                @getQuantity="updateOrderQty($event, row)"
                              />
                            </li>

                            <li
                              class="flex items-center justify-between bg-gray-50 rounded pl-4 pr-6 py-2"
                            >
                              <p class="font-medium text-theme-secondary">
                                Total:
                              </p>
                              <p class="font-medium text-theme-secondary">
                                ${{ commaFormat(getBulkTotal(row)) }}
                              </p>
                            </li>
                            <li
                              class="flex items-center justify-between rounded pl-2 pr-4 py-2"
                            >
                              <div>
                                <DisclosureButton
                                  v-if="row.bulk?.length > 0"
                                  class="bulk-icon flex items-center"
                                >
                                  <Icon
                                    name="package-outline"
                                    class="w-3.5 h-3.5 flex-shrink-0"
                                  />
                                  <span class="ml-2 text-black"
                                    >Bulk Pricing</span
                                  >
                                </DisclosureButton>
                              </div>

                              <button
                                class="flex items-center text-sm text-red-400"
                                type="button"
                                :aria-label="`Remove ${itemLabel(row)}`"
                                @click="removeOrder(row)"
                              >
                                <Icon
                                  class="w-4 h-4"
                                  name="delete"
                                />Remove
                              </button>
                            </li>
                          </ul>
                        </div>
                        <!-- <span
                          :id="row.id"
                          class="hidden text-red-600 2xl:inline-block"
                      >
                          {{ row.outofStock ?? "" }}
                        </span>-->
                      </div>
                    </td>
                    <td class="hidden xl:table-cell">
                      <DisclosureButton
                        v-if="row.bulk?.length > 0"
                        class="bulk-icon flex items-center"
                        v-tippy="{
                          content:
                            'Click here to see the bulk pricing discounts available for this product.',
                        }"
                      >
                        <Icon name="package-outline" class="w-4 h-4" />
                        <span class="ml-2 text-black">Bulk Pricing</span>
                      </DisclosureButton>
                    </td>
                    <td class="hidden xl:table-cell">
                      <PlusMinusInput
                        :label="itemLabel(row)"
                        v-model:modelValue="row.qty"
                        :max="quantityMax(row)"
                        :min="1"
                        @getQuantity="updateOrderQty($event, row)"
                      />
                    </td>
                    <td class="hidden xl:table-cell">
                      <p class="font-medium text-theme-secondary">
                        ${{ commaFormat(getBulkPrice(row)) }}
                      </p>
                    </td>
                    <td class="hidden xl:table-cell">
                      <div class="item-action">
                        <p>${{ commaFormat(getBulkTotal(row)) }}</p>
                        <button type="button" :aria-label="`Remove ${itemLabel(row)}`" @click="removeOrder(row)">
                          <Icon name="delete" aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  <DisclosurePanel
                    as="tr"
                    class="bulk-info-tr"
                    v-slot="{ close }"
                  >
                    <td colspan="5">
                      <div class="bulk-pricing-modal inline-info">
                        <div class="bulk-pricing-header">
                          <Icon name="package" class="pkg-icon" />
                          <div class="text-left">
                            <h3>Bulk Pricing</h3>
                            <p>
                              Automatic volume discounts for {{ row.name }}
                              <strong>${{ row.price }}</strong>
                            </p>
                          </div>
                          <button class="cls-btn" @click="closeBukInfo(close)">
                            <Icon name="close" />
                          </button>
                        </div>
                        <div class="bulk-pricing-content">
                          <table>
                            <thead class="bg-white">
                              <tr>
                                <th scope="col">QUANTITY</th>
                                <th scope="col">DISCOUNT</th>
                                <th scope="col">PRICE</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="brow in getBulkData(row)" :key="brow.id">
                                <td>{{ brow.min_quantity }}+</td>
                                <td>
                                  {{ Number(brow.discount_percentage).toFixed(2) }}%
                                </td>
                                <td>
                                  ${{ Number(brow.discounted_price).toFixed(2) }}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </td>
                  </DisclosurePanel>
                </Disclosure>
              </tbody>

            </table>
          </div>
          <p v-if="!selectedItems.length" class="empty-search-hint">Or search for products to add</p>
          <div class="listing-entry">
            <LightningProductSearch ref="productSearch" :search-client="searchClient" :is-visible="isItemVisible" :is-in-cart="inOrder" :add-product="manualAddProduct" :items="selectedItems" :prices="customPriceList" :notify-ids="notifyProduct" :notifying-ids="notifyingIds" :busy="pageBusy" @notify="Notify" @cancel-notify="toggleStockNotification($event, false)" @paste="$refs.orderImport.openPaste()" />
            <div class="listing-order-actions">
              <div class="listing-order-total" aria-live="polite"><strong>{{ selectedItems.length }} {{ selectedItems.length === 1 ? 'product' : 'products' }} · {{ orderUnitCount }} units</strong><span>Subtotal ${{ commaFormat(orderSubtotal) }}</span></div>
              <button type="button" class="listing-add-action" :disabled="!selectedItems.length || orderBusy" @click="addProductsIntoCart"><Icon v-if="cartAdding" name="reload" class="animate-spin" aria-hidden="true" />{{ cartAdding ? 'Adding…' : selectedItems.length ? `Add ${selectedItems.length} to order` : 'Add to order' }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Confirm
      :isVisible="showConfirmDialouge"
      cancelButton="Ok"
      confirmButton="stay here"
      @cancel="returnToRoute"
      @confirm="showConfirmDialouge = false"
    >
      <p>
        The products in lightning order haven't been added to your order. They
        will be saved here so you can come back and add them
      </p>
    </Confirm>

    <Confirm
      :width="48"
      :isVisible="showOutOfStockModal"
      title="These items are out of stock"
      cancelButton="Cancel"
      confirmButton="Remove All"
      @cancel="showOutOfStockModal = false"
      @confirm="removeOutOfStock()"
    >
      <div class="out-of-stock header">
        <div class="col-span-10 title">Product</div>
        <div class="col-span-2 title">Quantity</div>
      </div>
      <div
        class="out-of-stock"
        v-for="item in outOfStockProducts"
        :key="item.id"
      >
        <div class="col-span-10 flex items-center gap-4">
          <img :src="item.imageThumbnailUrl" alt="" />
          <div>
            <p>{{ item.name }}</p>
            <span>{{ item.brand }} - {{ item.product_type }}</span>
          </div>
        </div>
        <div class="col-span-2 text-sm text-theme-secondary font-medium">
          {{ item.qty }}
        </div>
      </div>
    </Confirm>

  </div>
</template>

<script>
import Mymixin from "@/modules/mixins";
import { ref } from "vue";
import Banner from "../common/Banner.vue";
import { inject } from "vue";
import { DotsHorizontalIcon } from "@heroicons/vue/solid";
import algoliasearch from "algoliasearch/lite";
import Bulk from "@/modules/Bulk";
import TestimonialSlider from "@/components/TestimonialSlider";
import GuestBanner from "@/components/GuestBanner";

import { Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import Segments from "@/modules/Segments";
import CsvOrderUpload, { downloadCsvRows, getOrderQuantityLimit, findDiscontinuedMatch, findSkuSuggestion, hasPrice } from "@/modules/CsvOrderUpload";
import LightningOrderImport from "@/components/LightningOrderImport";
import LightningProductSearch from "@/components/LightningProductSearch";
import CsvImportResults from "@/components/CsvImportResults";

export default {
  name: "Lightning",
  mixins: [Mymixin],
  data() {
    return {
      searchClient: algoliasearch(
        process.env.ALGOLIA_APP_ID,
        process.env.ALGOLIA_API_KEY
      ),
      selectedItems: [],
      customPriceList: {},
      notifyProduct: {},
      notifyingIds: [],
      showConfirmDialouge: false,
      navigateTo: null,
      showOutOfStockModal: false,
      width: 48,
      outOfStockProducts: [],
      showImportPanel: false,
      restoreAppOverflow: false,
      csvUploading: false,
      quickAdding: false,
      cartAdding: false,
      csvImportResult: null,
      csvImportIds: [],
      csvResultsDismissed: false,
    };
  },
  components: {
    Banner,
    TestimonialSlider,
    GuestBanner,
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    LightningOrderImport,
    LightningProductSearch,
    CsvImportResults,
    DotsHorizontalIcon,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
  },
  computed: {
    orderBusy() { return this.csvUploading || this.quickAdding || this.cartAdding; },
    // Page-wide lock only for imports and "Add to order". A single add from search
    // shows its own row spinner; methods still guard on orderBusy against races.
    pageBusy() { return this.csvUploading || this.cartAdding; },
    orderUnitCount() { return this.selectedItems.reduce((total, item) => total + Number(item.qty || 0), 0).toLocaleString("en-US"); },
    orderSubtotal() { return this.selectedItems.reduce((total, item) => total + Number(this.getBulkTotal(item)), 0).toFixed(2); },
    canUndoImport() { return this.selectedItems.some(item => this.csvImportIds.includes(item.id)); },
    cart() {
      return this.$store.getters.cart;
    },
    isCanna() {
      return this.$store.getters.getIsCanna;
    },
    getStateCode() {
      return this.$store.getters.getSelectedAddress?.state_code;
    },
  },
  created() {
    this.selectedItems = this.getOrder();
  },
  mounted() {
    const app = document.getElementById("app");
    this.restoreAppOverflow = Boolean(app && !app.classList.contains("enable-sticky"));
    if (this.restoreAppOverflow) app.classList.add("enable-sticky");

    if (this.selectedItems.length > 0) this.checkOrder();

    this.getCustomPriceList();
    this.getNotifyProduct();
  },
  beforeUnmount() {
    if (this.restoreAppOverflow) {
      document.getElementById("app")?.classList.remove("enable-sticky");
    }
  },
  methods: {
    quantityMax(item) { const limit = getOrderQuantityLimit(item); return Number.isFinite(limit) ? limit : 99000; },
    // Variant names repeat across products ("Red"), so include product and SKU.
    itemLabel(row) { return (row.name || row.productName || "item") + (row.sku ? ` (${row.sku})` : ""); },
    isItemVisible(val) {
      const isActive = val.product_active === 1 && val.product_status === 'active' && val.active === 1 && val.status === 'active';
      const isAllowedPriceList = this.cart.price_list_id == '33' || val.tags != 'GGV ONLY';
      const isCannaAllowed = !this.isCanna || val.is_canna == this.isCanna;
      const isNotBannedState = !val.banned_state?.includes(this.getStateCode);
      return isActive && isAllowedPriceList && isCannaAllowed && isNotBannedState;
    },
    returnToRoute() {
      this.navigateTo.isRoute = true;
      this.$router.push(this.navigateTo);
    },

    removeOutOfStock() {
      const nextItems = this.selectedItems.filter(item => !this.outOfStockProducts.some(row => row.id == item.id));
      this.setOrder(nextItems);
      this.selectedItems = nextItems;
      this.showOutOfStockModal = false;
    },
    getCustomPriceList() {
      if (this.axios.isLoggedIn) {
        this.axios.authApi.get("/customer-pricelist").then((res) => {
          this.customPriceList = res.data.data || {};
        }).catch(() => {});
      }
    },
    getNotifyProduct() {
      if (this.axios.isLoggedIn) {
        this.axios.authApi.get("/product-stock").then((res) => {
          this.notifyProduct = res.data.data || {};
        }).catch(() => {});
      }
    },
    async checkOrder(items = this.selectedItems) {
      if (this.axios.isLoggedIn) {
        const ids = items.map((r) => r.id);
        const res = await this.axios.authApi.post("/check-stock", {
          variant_ids: ids,
        });
        if (res.data.status === false || !Array.isArray(res.data.data)) throw new Error("Stock check failed");
        const checkStock = res.data.data;
        items.forEach((r) => {
          checkStock.forEach((el) => {
            if (el.id == r.id) {
              r.price = el.price;
              if (el.MaxOrderQty != null) r.MaxOrderQty = el.MaxOrderQty;
              if (el.stockLevelAbsolute != null) r.stockLevelAbsolute = el.stockLevelAbsolute;
              if (el.message) {
                r.outofStock = el.message;
                r.stockLevelAbsolute = 0;
              }
            }
          });
        });
      }
    },
    getOrder() {
      let data = JSON.parse(localStorage.getItem("lightning_order"));
      return data ? data.filter((d) => !!d) : [];
    },
    setOrder(item) {
      localStorage.setItem(
        "lightning_order",
        JSON.stringify(item.filter((d) => !!d))
      );
    },
    async importCsvFile(file) {
      return this.importOrder(file.name, () => this.resolveCsvFile({ file, axios: this.axios, isAlreadyAdded: item => this.isSelected(item) || this.inOrder(item.id) }));
    },
    async importPastedOrder(text) {
      return this.importOrder("Pasted SKUs", () => this.resolvePastedOrder({ text, axios: this.axios, isAlreadyAdded: item => this.isSelected(item) || this.inOrder(item.id) }));
    },
    async importOrder(source, resolve) {
      if (this.orderBusy) return { ok: false, error: "Wait for the current action to finish, then try again." };
      this.clearImportResult();
      this.csvUploading = true;
      try {
        const result = await resolve();
        if (!result.ok) return result;
        if (result.items.length) await this.checkOrder(result.items);
        const available = [];
        result.items.forEach(item => {
          const row = result.summary.addedRows.find(row => row.item.id == item.id);
          const limit = getOrderQuantityLimit(item);
          if (limit <= 0) {
            result.summary.outOfStockRows.push({ ...row, reason: "Out of stock" });
            result.summary.outOfStockSkus.push(item.sku);
            result.summary.cappedRows = result.summary.cappedRows.filter(row => row.item.id != item.id);
            result.summary.cappedSkus = result.summary.cappedRows.map(row => row.sku);
            return;
          }
          const qty = Math.min(item.qty, limit);
          if (qty !== item.qty) {
            const capped = result.summary.cappedRows.find(row => row.item.id == item.id);
            const reason = Number(item.MaxOrderQty) === limit ? "Maximum order quantity" : "Available stock";
            if (capped) { capped.addedQty = qty; capped.reason = reason; }
            else {
              result.summary.cappedRows.push({ ...row, addedQty: qty, reason });
              result.summary.cappedSkus.push(item.sku);
            }
          }
          available.push({ ...item, qty, importSource: source });
        });
        // Persist the complete next list before publishing it, so failures are retryable.
        const nextItems = [...this.selectedItems, ...available];
        this.setOrder(nextItems);
        this.selectedItems = nextItems;
        result.summary.successCount = available.length;
        this.csvImportIds = available.map(item => item.id);
        // Say when the list holds more than this import, so the bottom-bar total adds up.
        result.summary.listTotal = nextItems.length;
        result.summary.otherListCount = nextItems.length - available.length;
        this.csvImportResult = { source, summary: result.summary, error: "" };
        this.csvResultsDismissed = false;
        await this.$nextTick();
        this.$refs.importResults?.focusResult();
        return { ok: true, summary: result.summary };
      } catch (err) {
        // A firewall block has no CORS headers, so it surfaces with no status at all.
        console.error("[Lightning import] product lookup failed", err?.response?.status ?? "no status (network error or blocked request)", err?.message);
        return { ok: false, error: "We couldn’t import these products. Your list hasn’t changed. Please try again." };
      } finally { this.csvUploading = false; }
    },
    // Drop the previous import summary so a new attempt (or its validation
    // error) never appears next to stale Not found / Invalid rows.
    clearImportResult() {
      this.csvImportResult = null;
      this.csvImportIds = [];
    },
    async undoImport() {
      if (this.orderBusy) return;
      const remaining = this.selectedItems.filter(item => !this.csvImportIds.includes(item.id));
      this.setOrder(remaining);
      this.selectedItems = remaining;
      this.csvImportIds = [];
      this.csvImportResult = null;
      await this.$nextTick();
      this.$refs.importFileTrigger.focus();
    },
    exportOrder() { downloadCsvRows(this.selectedItems, "medusa-lightning-order.csv"); },
    getCsvMatchState(item) { return this.inOrder(item.id) ? "Already in order" : this.isSelected(item) ? "In your list" : ""; },
    async findCsvMatches(rows) {
      const response = await this.searchClient.search(rows.map(row => ({ indexName: "variants_index", params: { query: row.sku, hitsPerPage: 5 } })));
      const matches = Object.create(null);
      rows.forEach((row, index) => {
        const candidates = (response.results[index]?.hits || []).filter(item => this.isItemVisible(item) && getOrderQuantityLimit(item) > 0);
        const match = this.findSkuSuggestion(row.sku, candidates) || findDiscontinuedMatch(row.sku, response.results[index]?.hits);
        if (match) matches[row.sku] = match;
      });
      return matches;
    },
    async useCsvMatch({ row, item }) {
      const result = this.csvImportResult;
      if (!result || this.getCsvMatchState(item) || this.orderBusy) return;
      const added = await this.quickAddProduct({ item, qty: row.qty });
      if (!added.ok) { result.error = added.error; return; }
      const staged = this.selectedItems.find(product => product.id == item.id);
      staged.importSource = result.source;
      this.setOrder(this.selectedItems);
      this.csvImportIds.push(staged.id);
      result.error = "";
      result.summary.successCount++;
      result.summary.missingRows = result.summary.missingRows.filter(entry => entry.row !== row.row);
      result.summary.missingSkus = result.summary.missingRows.map(entry => entry.sku);
      if (added.capped) {
        result.summary.cappedRows.push({ ...row, item: staged, requestedQty: row.qty, addedQty: added.qty, reason: "Maximum order quantity or available stock" });
        result.summary.cappedSkus.push(staged.sku);
      }
    },
    async quickAddProduct({ item, qty }) {
      if (this.orderBusy) return { ok: false, error: "Wait for the current action to finish." };
      if (!Number.isSafeInteger(qty) || qty < 1) return { ok: false, error: "Use a whole-number quantity of 1 or more." };
      if (this.inOrder(item.id)) return { ok: false, error: "This product is already in your order." };
      this.quickAdding = true;
      try {
        const response = await this.axios.authApi.get("/get-product/" + item.id);
        const product = response.data?.data;
        if (response.data?.status === false || !product || !this.isAllowed(product)) return { ok: false, error: "This product is unavailable for your account or shipping address." };
        await this.checkOrder([product]);
        if (!hasPrice(product)) return { ok: false, error: "This product has no price for your account yet. Contact your rep to order it." };
        const limit = getOrderQuantityLimit(product);
        if (limit <= 0) return { ok: false, error: "This product is now out of stock. Choose another product or request a notification." };
        const nextQty = Math.min(qty, limit);
        const existing = this.selectedItems.find(row => row.id == product.id);
        const updated = { ...product, qty: nextQty, ...(existing?.importSource ? { importSource: existing.importSource } : {}) };
        const nextItems = existing ? this.selectedItems.map(row => row.id == product.id ? updated : row) : [...this.selectedItems, updated];
        this.setOrder(nextItems);
        this.selectedItems = nextItems;
        return { ok: true, qty: nextQty, capped: nextQty !== qty };
      } catch (err) { return { ok: false, error: "We couldn’t add this product. Your list hasn’t changed. Please try again." }; }
      finally { this.quickAdding = false; }
    },
    // Manual edits make the import summary misleading ("0 of 1 added"), so
    // collapse it. Adding a suggested match from the banner itself does not.
    collapseImportResult() {
      if (this.csvImportResult) this.csvResultsDismissed = true;
    },
    async manualAddProduct(args) {
      const result = await this.quickAddProduct(args);
      if (result.ok) this.collapseImportResult();
      return result;
    },
    updateOrderQty(qty, item) {
      this.collapseImportResult();
      const updatedItems = this.selectedItems.map((el) => {
        if (el.id == item.id) {
          el = item;
          el.qty = qty[0];
        }
        return el;
      });
      this.selectedItems = updatedItems;
      this.setOrder(this.selectedItems);
    },
    removeOrder(item) {
      if (item) {
        this.collapseImportResult();
        this.selectedItems = this.selectedItems.filter((r) => r.id != item.id);
        this.setOrder(this.selectedItems);
      } else {
        this.selectedItems = [];
        localStorage.removeItem("lightning_order");
      }
    },
    isSelected(item) {
      let is = this.selectedItems.filter((r) => r.id == item.id);
      return is.length > 0 ? true : false;
    },
    async addProductsIntoCart() {
      if (!this.selectedItems.length || this.orderBusy) return;
      // Lists saved before prices were checked can still hold unpriced items; never send them at $0.
      const unpriced = this.selectedItems.filter(item => !hasPrice(item));
      if (unpriced.length) {
        this.$store.dispatch("setToast", { status: true, msg: `Remove ${unpriced.map(item => item.sku).join(", ")} first: no price is set for your account.`, type: "danger" });
        return;
      }
      this.cartAdding = true;
      try {
        const items = this.selectedItems.map(item => ({ variant_id: item.id, qty: item.qty }));
        const response = await this.axios.authApi.post("/add-products-to-cart", { items });
        if (response.data.status) {
          const addedItems = [...this.selectedItems];
          await this.$store.dispatch("UpdateBlukIntoCart", response.data.data);
          // The production API returns the cart as it was before these items were added
          // (cached on the user), so refetch it to update the header count and drawer.
          this.$store.dispatch("getCart", { ax: this.axios });
          this.removeOrder();
          this.csvImportResult = null;
          this.csvImportIds = [];
          // Confirm what happened; the list clears, so without this the page gave no feedback.
          const count = addedItems.length;
          const warnings = Array.isArray(response.data.warnings) ? response.data.warnings : [];
          this.$store.dispatch("setToast", {
            status: true,
            type: "success",
            msg: `${count} ${count === 1 ? "product" : "products"} added to your order${warnings.length ? `. ${warnings.join(" ")}` : "."}`,
          });
          // Tracking must never turn an accepted order update into a retry.
          try { this.ProductsAdded(addedItems); } catch (err) { /* optional analytics */ }
        } else {
          const unavailable = Array.isArray(response.data.data) ? response.data.data : [];
          this.outOfStockProducts = this.selectedItems.filter(item => unavailable.some(row => row.variant_id == item.id));
          if (this.outOfStockProducts.length) this.showOutOfStockModal = true;
          else this.$store.dispatch("setToast", { status: true, msg: response.data.error || "These products couldn’t be added. Please try again.", type: "danger" });
        }
      } catch (err) {
        this.$store.dispatch("setToast", { status: true, msg: "We couldn’t confirm the update. Your list is saved. Check your order before trying again.", type: "danger" });
      } finally { this.cartAdding = false; }
    },
    inOrder(variant_id) {
      return (this.cart?.itemsByCat || []).some(category => (category.items || []).some(item => item.variantId == variant_id));
    },
    async Notify(item) { return this.toggleStockNotification(item, true); },
    async toggleStockNotification(item, enabled) {
      if (this.notifyingIds.includes(item.id) || Boolean(this.notifyProduct[item.id]) === enabled) return;
      this.notifyingIds.push(item.id);
      try {
        const response = await this.axios.authApi.post(enabled ? "/notify" : "/delete-notify", { variant_id: item.id });
        if (!response.data.status) throw new Error("Notification failed");
        if (enabled) this.notifyProduct[item.id] = true;
        else delete this.notifyProduct[item.id];
        try { this.ProductNotify(enabled ? "add" : "remove", this.$route, item, { ...item, name: item.productName || item.name }, "Lightning Order"); } catch (err) { /* optional analytics */ }
      } catch (err) {
        this.$store.dispatch("setToast", { status: true, msg: "We couldn’t save this stock notification. Please try again.", type: "danger" });
      } finally { this.notifyingIds = this.notifyingIds.filter(id => id !== item.id); }
    },
    async notifyAll(items) {
      for (const item of items) if (!this.notifyProduct[item.id]) await this.Notify(item);
    },
  },
  beforeRouteLeave(to, from) {
    if (!this.isLoggedIn) {
      return true;
    }
    if (this.navigateTo ? this.navigateTo.isRoute : 0) {
      return true;
    }
    if (this.selectedItems.length > 0) {
      this.showConfirmDialouge = true;
      this.navigateTo = to;
      return false;
    }
  },
  beforeMount() {
    this.$store.dispatch("getCart", { ax: null });
  },
  setup() {
    const axios = inject("axios");
    const { getBulkPrice, getBulkTotal } = Bulk();
    const showBulkPricing = ref(false);
    const isLoggedIn = axios.isLoggedIn;

    const { ProductsAdded, ProductNotify } = Segments();
    const { resolveCsvFile, resolvePastedOrder, isAllowed, downloadCsvTemplate } = CsvOrderUpload();

    const bulk = ref();

    function ShowBluk(val) {
      bulk.value = val;
      showBulkPricing.value = true;
    }

    const getBulkData = (row) => {
      let data = row.bulk || [];
      if (data.length) {
        data = [];

        for (const bd of row.bulk) {
          let discountedPrice =
            row.wholesale_price -
            (row.wholesale_price / 100) * Number(bd.discount_percentage);

          discountedPrice = Number(discountedPrice.toFixed(2));

          if (discountedPrice >= Number(row.price)) {
            discountedPrice = Number(row.price);
          }

          if (
            row.min_price &&
            discountedPrice < Number(row.min_price)
          ) {
            discountedPrice = Number(row.min_price);
          }
          const discountPercentage = ((Number(row.price) - discountedPrice) / Number(row.price)) * 100;
          const discountPercentageRounded = Number(discountPercentage.toFixed(2));

          const last = data[data.length - 1];
          if (discountPercentage > 0 && (!last || last.discount_percentage !== discountPercentageRounded)) {
            data.push({
              min_quantity: bd.min_quantity,
              discount_percentage: discountPercentageRounded,
              discounted_price: discountedPrice,
            });
          }
        }
      }
      return data;
    };

    // function isBulkPrice(row){
    //   if(row.bulk.length>0){
    //     const dis = row.bulk.filter(r=> row.qty > r.min_quantity)
    //     const discount_row = dis[dis.length-1]
    //     return discount_row?(row.price-(row.price*discount_row.discount_percentage)/100).toFixed(2): (Number(row.price).toFixed(2))
    //   }
    //   else{
    //     return (Number(row.price).toFixed(2))
    //   }

    //   // (bulk.price - (bulk.price*row.discount_percentage)/100).toFixed(2)
    // }

    const closeBukInfo = (close) => close();

    return {
      getBulkPrice,
      getBulkTotal,
      ProductsAdded,
      ProductNotify,
      resolvePastedOrder,
      isAllowed,
      downloadCsvTemplate,
      findSkuSuggestion,
      bulk,
      axios,
      showBulkPricing,
      ShowBluk,
      isLoggedIn,
      closeBukInfo,
      getBulkData,
      resolveCsvFile,
    };
  },
};
</script>

<style lang="scss" scoped>
.collection-banner-container {
  background-image: url("/images/lines-pattern.png");

  &.bg-full-image {
    @apply bg-cover;
  }
}

.lightning-listing {
  @apply py-8 space-x-10 flex items-start pb-16;
  @include maxContainer();

  .listing-header {
    @apply flex flex-col lg:flex-row lg:items-center justify-between mb-5 gap-4;

    h3 {
      @apply text-2xl text-brand-primary font-semibold;
    }

    .add-action-btn {
      @apply w-48 bg-brand-primary text-white uppercase font-normal tracking-wide text-xs py-2.5 px-3 rounded;
    }

    .btn-disabled {
      @apply w-48 bg-gray-500 cursor-auto pointer-events-none text-white uppercase font-normal tracking-wide text-xs py-2.5 px-3 rounded;
    }
  }

  .listing-contents {
    @apply bg-white rounded-lg;

    table {
      @apply min-w-full divide-y divide-gray-100;

      thead {
        @apply bg-white;

        th {
          @apply px-4 py-2.5 text-left font-medium text-brand-body uppercase tracking-wider;
          font-size: 11px;
        }
      }

      tbody {
        @apply bg-white divide-y divide-gray-100;

        td {
          @apply px-4 py-2 whitespace-nowrap text-xs text-theme-body;
          &.name {
            @apply font-medium text-gray-900 group;
          }

          .bulk-icon {
            @apply text-theme-secondary flex items-center border-b border-dashed border-theme-secondary;
          }

          .item-info {
            @apply flex-grow;
            .inner {
              @apply flex items-center space-x-3 flex-shrink-0 flex-grow;
              img {
                @apply w-8 h-8 object-contain object-center;
              }

              p {
                @apply text-xs leading-4 text-theme-secondary font-medium;
              }

              span {
                @apply text-xs leading-4 text-theme-body font-normal;

                &.error {
                  @apply text-red-600 text-xs;
                }
              }
            }

            .on-mobile {
              @apply flex-grow xl:hidden flex-col space-y-1 pl-6 py-2;

              .bulk-icon {
                span {
                  @apply text-sm;
                }
              }
            }
          }

          .item-action {
            @apply flex items-center space-x-5 flex-shrink-0 flex-grow justify-between;
            p {
              @apply font-medium text-theme-secondary;
            }

            button {
              @apply transition cursor-pointer bg-transparent focus:outline-none;
              svg {
                @apply w-5 h-5;
              }
            }
          }
        }
      }
    }
  }

  .inline-info {
    @apply w-auto -mx-6 md:w-full md:mx-0;
    max-width: unset;

    .pkg-icon {
      @apply hidden md:inline-block;
    }
  }

  .bulk-pricing-content {
    table {
      thead {
        @apply bg-white;

        th {
          @apply px-3 py-3 md:py-3.5 md:px-4 text-sm md:text-md;
        }
      }

      tbody {
        td {
          @apply px-3 py-3 md:py-3.5 md:px-4 text-sm md:text-md;
        }
      }
    }
  }

  .listing-header-actions { @apply flex flex-wrap items-center gap-1.5; }
  .listing-import-action { @apply inline-flex items-center justify-center gap-1.5 bg-gray-200 text-theme-secondary text-xs font-medium px-2.5 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-indigodark; min-height: 32px; svg { @apply w-3.5 h-3.5; } &.is-icon { @apply w-8 px-0; } &:disabled { @apply opacity-50 cursor-not-allowed; } }
  .listing-options-menu { @apply absolute right-0 top-full mt-2 p-1 bg-white rounded border border-gray-200 shadow-box focus:outline-none; width: 190px; z-index: 40; button { @apply flex items-center gap-2 w-full rounded text-left px-2.5 text-xs text-brand-primary; min-height: 32px; svg { @apply w-3.5 h-3.5; } &.is-active { @apply bg-theme-purple-light; } } }
  .empty-search-hint { @apply text-center text-sm text-theme-dark py-4; }
  .listing-entry { @apply sticky bottom-0 z-10 grid items-end gap-5 p-5 bg-white border-t border-gray-100 rounded-b-lg; grid-template-columns: minmax(0, 1fr) auto; box-shadow: 0 -4px 12px rgba(36, 39, 53, 0.04); padding-bottom: calc(1.25rem + env(safe-area-inset-bottom, 0px)); }
  .listing-order-actions { @apply flex items-center gap-4; }
  .listing-order-total { @apply flex flex-col gap-1 text-xs text-theme-dark; strong { @apply font-medium text-brand-primary; } span { @apply whitespace-nowrap; } }
  .listing-add-action { @apply inline-flex items-center justify-center gap-2 bg-brand-primary text-white uppercase text-xs font-medium tracking-wide px-5 py-3 rounded hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-brand-indigodark focus:ring-offset-2; min-height: 44px; svg { @apply w-4 h-4; } &:disabled { @apply bg-gray-300 text-theme-dark cursor-not-allowed; } }
  .import-source-badge { @apply inline-flex items-center self-start px-1.5 py-0.5 rounded bg-theme-purple-light text-brand-indigodarkest; font-size: 10px; line-height: 14px; }
  .listing-contents table tbody td .item-info .inner p { @apply leading-4; white-space: normal; overflow-wrap: anywhere; }
  .listing-contents table tbody td .item-info .inner span { @apply leading-4; white-space: normal; }
  .listing-contents table tbody td .item-info .inner .import-source-badge { @apply text-brand-indigodarkest; font-size: 10px; line-height: 14px; }
  :deep(.listing-contents .mpm-input) { min-height: 32px; gap: 0; padding: 0; input { width: 34px; height: 30px; } button { width: 26px; height: 30px; display: inline-flex; align-items: center; justify-content: center; } }
  :deep(.listing-contents .error-info) { display: block; position: static; transform: none; width: 100%; margin-top: 2px; padding: 2px 4px; border-radius: 4px; font-size: 10px; line-height: 12px; }
  .listing-contents .item-action > button, .listing-contents .on-mobile li > button { min-width: 32px; min-height: 32px; }
  @media (max-width: 1100px) { .listing-entry { grid-template-columns: minmax(0, 1fr); } .listing-order-actions { @apply justify-between; } }
  @media (max-width: 640px) {
    @apply py-5 pb-8;
    .listing-header { @apply mb-4 gap-3; h3 { @apply text-xl; } }
    .listing-entry { @apply p-3 gap-3; padding-bottom: calc(0.75rem + env(safe-area-inset-bottom, 0px)); }
    .listing-contents table tbody td { @apply px-2.5 py-2; white-space: normal; }
    .listing-contents table tbody td .item-info { @apply min-w-0; .inner { @apply items-start gap-2; > a { @apply min-w-0; } } .on-mobile { @apply pl-0 pb-0; } }
    .listing-order-actions { @apply gap-2; }
    .listing-add-action { @apply px-3; }
    .listing-order-total { font-size: 11px; }
  }

}

.notify-bell {
  @apply flex items-center space-x-3 uppercase text-xs;
  .n-icon {
    @apply inline-flex items-center justify-center w-10 h-10 bg-brand-red text-white rounded;

    svg {
      @apply w-5 h-5;
    }
  }

  &.undo {
    .n-icon {
      @apply bg-theme-secondary;
    }
  }
}

.save-time {
  @apply flex flex-col-reverse items-center xl:grid xl:grid-cols-2 px-6 py-8 xl:p-16 bg-white rounded-lg;
  box-shadow: 0px 4px 40px rgba(0, 0, 0, 0.04);

  img {
    @apply max-w-full px-5 py-8 xl:py-0;
  }

  .info {
    @apply flex flex-col items-start justify-center space-y-4 text-left h-full px-5;

    h3 {
      @apply text-3xl font-semibold text-theme-secondary leading-normal;
    }

    p {
      @apply text-base font-light text-theme-body;
    }

    button,
    .link {
      @apply bg-transparent border-none focus:outline-none text-base font-medium hover:underline text-theme-primary;
    }
  }
}

.out-of-stock {
  @apply grid grid-cols-12 gap-4;

  &.header {
    @apply border-b pb-3 border-gray-100;

    &.title {
      @apply text-left text-sm font-medium text-brand-body uppercase tracking-wider;
    }
  }

  p {
    @apply text-sm leading-none text-theme-secondary font-medium;
  }

  img {
    @apply w-5 h-5 md:w-10 md:h-10 object-contain object-center;
  }

  span {
    @apply text-sm leading-none text-theme-body font-normal;
  }
}
</style>
